import { useCallback, useMemo, useRef, useState } from 'react';
import { TriggerFetchLatestDocForEmbed } from '../utils/TriggerFetchLatestDocForEmbed';

// In-memory cache (per-tab)
const memoryCache = new Map(); // key -> { url, expiresAtMs, lastGenerated, createdAtMs }

const nowMs = () => Date.now();

function makeKey(patientId, reportStage, lastGenerated) {
  // Include lastGenerated to ensure freshness across regenerations
  return `${patientId}::${reportStage}::${String(lastGenerated || '')}`;
}

function readSessionCache(key) {
  try {
    const raw = sessionStorage.getItem(`embedUrlCache:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed || null;
  } catch {
    return null;
  }
}

function writeSessionCache(key, value) {
  try {
    sessionStorage.setItem(`embedUrlCache:${key}`, JSON.stringify(value));
  } catch {
    // ignore quota errors
  }
}

export function useEmbeddedReportUrl(patientId, reportStage, lastGenerated, clinicianId) {
  const [url, setUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(null); // avoid duplicate calls

  const key = useMemo(() => makeKey(patientId, reportStage, lastGenerated), [patientId, reportStage, lastGenerated]);

  const getFromCacheIfValid = useCallback(() => {
    const entry = memoryCache.get(key) || readSessionCache(key);
    if (!entry) return null;
    const safetyCutoffMs = Math.max(0, (new Date(entry.expiresAt).getTime() || 0) - 60_000); // expiresAt - 60s
    if (nowMs() >= safetyCutoffMs) {
      try { console.log('[useEmbeddedReportUrl] cache stale/near-expiry'); } catch {}
      return null; // too close to expiry
    }
    // lastGenerated match is embedded in key; if key matches, we assume it matches
    try { console.log('[useEmbeddedReportUrl] cache hit'); } catch {}
    return entry;
  }, [key]);

  const saveToCache = useCallback((data) => {
    const entry = {
      url: data.embedUrl,
      expiresAt: data.expiresAt,
      lastGenerated: data.lastGenerated ?? lastGenerated,
      createdAtMs: nowMs(),
    };
    memoryCache.set(key, entry);
    writeSessionCache(key, entry);
    return entry;
  }, [key, lastGenerated]);

  const fetchUrl = useCallback(async () => {
    const cached = getFromCacheIfValid();
    if (cached) {
      setUrl(cached.url);
      setError(null);
      return cached.url;
    }

    if (inFlightRef.current) return inFlightRef.current; // return existing promise

    setIsLoading(true);
    setError(null);
    const start = performance.now();
    const p = TriggerFetchLatestDocForEmbed({ reportStage, clinicianId, patientId })
      .then((res) => {
        // Note: Trigger wrapper uses auth.currentUser on server; clinicianId field is not required
        if (!res.success) throw new Error(res.error || 'Failed to fetch embed URL');
        const entry = saveToCache(res);
        setUrl(entry.url);
        return entry.url;
      })
      .catch((e) => {
        // If we have a stale cached entry, try to surface it as best-effort fallback
        const stale = memoryCache.get(key) || readSessionCache(key);
        if (stale && stale.url) setUrl(stale.url);
        setError(e?.message || 'Failed to fetch document');
        throw e;
      })
      .finally(() => {
        setIsLoading(false);
        const ms = Math.round(performance.now() - start);
        try { console.log(`[useEmbeddedReportUrl] fetch latency: ${ms}ms`); } catch {}
        inFlightRef.current = null;
      });
    inFlightRef.current = p;
    return p;
  }, [getFromCacheIfValid, key, patientId, reportStage, saveToCache, clinicianId]);

  const prefetch = useCallback(async () => {
    try {
      const cached = getFromCacheIfValid();
      if (cached) return;
      await fetchUrl();
    } catch {
      // prefetch best-effort
    }
  }, [fetchUrl, getFromCacheIfValid]);

  const refresh = useCallback(async () => {
    try {
      // Clear cache for this key then fetch fresh
      memoryCache.delete(key);
      try { sessionStorage.removeItem(`embedUrlCache:${key}`); } catch {}
      return await fetchUrl();
    } catch (e) {
      throw e;
    }
  }, [fetchUrl, key]);

  return { url, isLoading, error, prefetch, fetch: fetchUrl, refresh };
}

export default useEmbeddedReportUrl;


